/**
 * <ky-why-it-works> — accordion list that crossfades a shared image as
 * each row opens. Only one row is open at a time. Vanilla, scoped to the
 * section instance, no external dependencies.
 */
class KyWhyItWorks extends HTMLElement {
  /** @type {HTMLElement[]} */
  #rows = [];

  /** @type {HTMLElement[]} */
  #images = [];

  connectedCallback() {
    this.#rows = Array.from(this.querySelectorAll('[data-row]'));
    this.#images = Array.from(this.querySelectorAll('[data-image]'));

    this.#rows.forEach((row) => {
      const header = row.querySelector('[data-row-header]');
      header?.addEventListener('click', () => this.toggle(row));
    });

    // Theme editor: opening a block in the sidebar opens the matching row.
    this.addEventListener('shopify:block:select', (event) => {
      const blockId = event.detail?.blockId;
      if (!blockId) return;
      const row = this.querySelector(`[data-row][data-block-id="${blockId}"]`);
      if (row) this.open(row);
    });
  }

  /**
   * Opens the row if it's closed, or closes it (back to the default image)
   * if it's already open — clicking the "−" on an open row closes it.
   * @param {HTMLElement} targetRow
   */
  toggle(targetRow) {
    if (targetRow.classList.contains('is-open')) {
      this.closeAll();
    } else {
      this.open(targetRow);
    }
  }

  /**
   * Opens the given row, closes all others, and swaps the active image.
   * @param {HTMLElement} targetRow
   */
  open(targetRow) {
    this.#rows.forEach((row) => {
      const header = row.querySelector('[data-row-header]');
      const isTarget = row === targetRow;
      row.classList.toggle('is-open', isTarget);
      header?.setAttribute('aria-expanded', isTarget ? 'true' : 'false');
    });

    const index = targetRow.dataset.index;
    // Fall back to the default image when this block has none of its own.
    const hasOwnImage = this.#images.some((img) => img.dataset.image === index);
    const activeKey = hasOwnImage ? index : 'default';

    this.#images.forEach((img) => {
      img.classList.toggle('is-active', img.dataset.image === activeKey);
    });
  }

  /** Closes every row and shows the section's default image. */
  closeAll() {
    this.#rows.forEach((row) => {
      const header = row.querySelector('[data-row-header]');
      row.classList.remove('is-open');
      header?.setAttribute('aria-expanded', 'false');
    });

    this.#images.forEach((img) => {
      img.classList.toggle('is-active', img.dataset.image === 'default');
    });
  }
}

if (!customElements.get('ky-why-it-works')) {
  customElements.define('ky-why-it-works', KyWhyItWorks);
}
